/**
 * React Query hook for Expenses List V2 with RDS-based pagination
 * Uses the new listExpensesV2 API with improved filtering and sorting
 */

import { useQuery } from '@tanstack/react-query';
import { listExpensesV2 } from 'services/billingServices';
import { queryKeys } from '../../../config/queryClient';

/**
 * Fetches expenses data with RDS-based pagination
 * API Response format: { total_count, data: [], page_count, page, limit }
 *
 * @param {Object} params - Request parameters
 * @returns {Promise<Object>} - Response with data array and pagination metadata
 */
const fetchExpensesV2 = async ({
  limit = 10,
  page = 1,
  expense_status = '',
  practice_area_list = [],
  case_id = '',
  expense_date_lower = '',
  expense_date_upper = '',
  sort_by,
  sort_order,
  search_key = '',
  full_list = false,
}) => {
  try {
    const response = await listExpensesV2({
      limit,
      page,
      expense_status,
      practice_area_list,
      case_id,
      expense_date_lower,
      expense_date_upper,
      sort_by,
      sort_order,
      search_key,
      full_list,
    });

    if (response?.data) {
      return response.data;
    }

    // Return empty structure if no data (matching API format)
    return {
      total_count: 0,
      data: [],
      page_count: 0,
      page: page,
      limit: limit,
    };
  } catch (error) {
    console.error('Error fetching expenses V2:', error);
    throw error;
  }
};

/**
 * Hook to fetch expenses with RDS-based pagination using React Query
 *
 * @param {Object} options - Configuration options
 * @param {number} options.limit - Items per page (default: 10)
 * @param {number} options.page - Page number (default: 1)
 * @param {string} options.expense_status - Filter by status (e.g., 'UNINVOICED', 'INVOICED')
 * @param {Array} options.practice_area_list - Filter by practice area IDs
 * @param {string} options.case_id - Filter by case ID
 * @param {string} options.expense_date_lower - Start date filter (timezone format)
 * @param {string} options.expense_date_upper - End date filter (timezone format)
 * @param {string} options.sort_by - Field to sort by
 * @param {string} options.sort_order - Sort order ('asc' or 'desc')
 * @param {string} options.search_key - Search keyword for filtering expenses
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} Query result object
 * @returns {Array} returns.expenses - Array of expense objects
 * @returns {number} returns.totalRecords - Total number of records
 * @returns {number} returns.currentPage - Current page number
 * @returns {number} returns.totalPages - Total number of pages
 * @returns {number} returns.pageSize - Items per page
 * @returns {boolean} returns.isLoading - Initial loading state
 * @returns {boolean} returns.isFetching - Fetching state (including background refetch)
 * @returns {boolean} returns.isError - Error state indicator
 * @returns {Object|null} returns.error - Error object if request failed
 * @returns {Function} returns.refetch - Function to manually refetch data
 *
 * @example
 * const {
 *   expenses,
 *   totalRecords,
 *   totalPages,
 *   isLoading,
 *   refetch
 * } = useExpenseListV2Query({
 *   limit: 10,
 *   page: 1,
 *   expense_status: 'UNINVOICED',
 *   practice_area_list: ['PA#001'],
 *   sort_by: 'expense_date',
 *   sort_order: 'desc',
 * });
 */
export const useExpenseListV2Query = ({
  limit = 10,
  page = 1,
  expense_status = '',
  practice_area_list = [],
  case_id = '',
  expense_date_lower = '',
  expense_date_upper = '',
  sort_by,
  sort_order,
  search_key = '',
  full_list = false,
  enabled = true,
} = {}) => {
  // Create stable query key based on all filter parameters
  const queryKey = queryKeys.expenses.listV2(
    {
      expense_status,
      practice_area_list,
      case_id,
      expense_date_lower,
      expense_date_upper,
      sort_by,
      sort_order,
      search_key,
      full_list,
    },
    page,
    limit
  );

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      fetchExpensesV2({
        limit,
        page,
        expense_status,
        practice_area_list,
        case_id,
        expense_date_lower,
        expense_date_upper,
        sort_by,
        sort_order,
        search_key,
        full_list,
      }),
    enabled,
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes - serves from cache before that
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes (React Query v4 uses gcTime instead of cacheTime)
    keepPreviousData: true, // Keep previous data while fetching
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: enabled, // Only refetch on mount if query is enabled
    retry: (failureCount) => {
      // Retry twice for errors
      return failureCount < 2;
    },
  });

  return {
    expenses: data?.data || [],
    totalRecords: data?.total_count || 0,
    currentPage: data?.page || page,
    totalPages: data?.page_count || 0,
    pageSize: data?.limit || limit,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};

export default useExpenseListV2Query;
